import i18n from '../i18n'

// Textos traduzidos que vêm da API (TextMap) usam as chaves base dos idiomas da cabine,
// nunca o código da UI: pt-BR vira pt.
export const TEXT_LANGS = ['pt', 'en', 'fr', 'es', 'hy']

const UI_LANG = { pt: 'pt-BR', en: 'en', fr: 'fr', es: 'es', hy: 'hy' }

export function textLang(uiLang = i18n.language) {
  const base = String(uiLang || '').split('-')[0].toLowerCase()
  return TEXT_LANGS.includes(base) ? base : 'en'
}

const filled = (v) => typeof v === 'string' && v.trim() !== ''

// pickText segue a regra do contrato: chave exata, senão en, senão a primeira chave não
// vazia, senão o fallback.
export function pickText(map, lang, fallback = '') {
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    const l = textLang(lang)
    if (filled(map[l])) return map[l]
    if (filled(map.en)) return map.en
    for (const v of Object.values(map)) if (filled(v)) return v
  }
  return fallback ?? ''
}

// cleanTextMap devolve só as chaves conhecidas com texto, aparado. É o que vai no corpo
// dos POST/PUT: chave fora da lista é 400 no backend.
export function cleanTextMap(map) {
  const out = {}
  for (const l of TEXT_LANGS) {
    const v = map?.[l]
    if (filled(v)) out[l] = v.trim()
  }
  return out
}

export function emptyTextMap() {
  return Object.fromEntries(TEXT_LANGS.map(l => [l, '']))
}

export const HOLE_TYPE_CODES = ['BLAST', 'REDRILL', 'PRESPLIT', 'AUXILIARY', 'GRADE_CONTROL']
export const MATERIAL_TYPE_CODES = ['ORE', 'WASTE']
export const CLASSIFICATION_CODES = ['FRESH', 'TRANSITION', 'OXIDE', 'TOPSOIL']
export const GRADING_CODES = ['VERY_HIGH_GRADE', 'HIGH_GRADE', 'MEDIUM_GRADE', 'LOW_GRADE', 'NAG', 'PAG']

export const CATALOG_CODE_RE = /^[A-Z][A-Z0-9_]{0,31}$/

// Só um código de verdade vira chave i18n: o 0.7 manda rótulos livres ("Desmonte") nos
// mesmos campos, e um ':' ou '.' no texto seria lido como namespace ou caminho.
function localCode(group, code, lang) {
  if (!code || !CATALOG_CODE_RE.test(code)) return null
  const key = `common:vmc_codes.${group}.${code}`
  if (!i18n.exists(key)) return null
  return lang ? i18n.getFixedT(UI_LANG[textLang(lang)] || lang)(key) : i18n.t(key)
}

// localTextMap: os rótulos que a própria UI tem para um código fixo, nos cinco idiomas.
// Serve de esqueleto quando o catálogo da API não tem o tipo.
export function localTextMap(group, code) {
  const out = {}
  for (const l of TEXT_LANGS) {
    const v = i18n.getResource(UI_LANG[l], 'common', `vmc_codes.${group}.${code}`)
    if (filled(v)) out[l] = v
  }
  return out
}

function catalogType(catalog, typeCode) {
  const types = Array.isArray(catalog?.types) ? catalog.types : []
  return types.find(t => t.code === typeCode) || null
}

// O code da classificação ou do teor é único dentro do tipo; sem o tipo (ou com um tipo
// que não o tem) procura nos dois, porque o 0.8 permite o mesmo code em ORE e WASTE.
function catalogEntry(catalog, list, typeCode, code) {
  const types = Array.isArray(catalog?.types) ? catalog.types : []
  const own = typeCode ? catalogType(catalog, typeCode) : null
  const ordered = own ? [own, ...types.filter(t => t !== own)] : types
  for (const t of ordered) {
    const hit = (Array.isArray(t[list]) ? t[list] : []).find(e => e.code === code)
    if (hit) return hit
  }
  return null
}

export function holeTypeLabel(code, lang) {
  if (!code) return ''
  return localCode('hole_types', code, lang) ?? String(code)
}

export function materialTypeLabel(code, catalog, lang) {
  if (!code) return ''
  return pickText(catalogType(catalog, code)?.labels, lang, '')
    || localCode('material_types', code, lang)
    || String(code)
}

export function classificationLabel(code, typeCode, catalog, lang) {
  if (!code) return ''
  return pickText(catalogEntry(catalog, 'classifications', typeCode, code)?.labels, lang, '')
    || localCode('classifications', code, lang)
    || String(code)
}

export function gradingLabel(code, typeCode, catalog, lang) {
  if (!code) return ''
  return pickText(catalogEntry(catalog, 'gradings', typeCode, code)?.labels, lang, '')
    || localCode('gradings', code, lang)
    || String(code)
}

// stopReasonLabel: o motivo pelo CÓDIGO do catálogo de paragem; sem o motivo no cache,
// o texto que o device mandou e, por último, o próprio código.
export function stopReasonLabel(code, deviceText, reasons, lang) {
  const r = code && Array.isArray(reasons) ? reasons.find(x => x.code === code) : null
  if (r) return pickText(r.labels, lang, r.label || code)
  return deviceText || code || ''
}
